<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { NButton, NCard, NForm, NFormItem, NInput, NSkeleton, NSpace, NTabPane, NTabs } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useSessionStore } from '../stores/session'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'
import RouteShell from './RouteShell.vue'

const route = useRoute()
const router = useRouter()
const wikiPages = useWikiPagesStore()
const session = useSessionStore()
const { pages } = storeToRefs(wikiPages)
const { currentUser } = storeToRefs(session)

// Edit mode is driven by the route name: `wikiPageEdit` prefills an
// existing page (path locked), `wikiPageNew` gives a blank form. We
// pull the id out of the pages catalog using the URL path so the edit
// route stays pretty (`/p/entities/foo.md/edit`) instead of an id URL.
// Static hint kept near the field it annotates so the wording stays in
// sync with PathValidator rules (app/services/wiki/path_validator.rb).
const pathHint = 'Use a category prefix (entities/, concepts/, summaries/) or one of the allow-listed top-level files.'

const isEdit = computed(() => route.name === 'wikiPageEdit')
const editingPath = computed(() => {
  const raw = route.params.path
  return Array.isArray(raw) ? raw.join('/') : (raw ?? '')
})

const path = ref('')
const content = ref('')

// Live markdown preview so the author isn't editing blind.
const wikiMarkdown = useWikiMarkdown()
const renderedPreview = computed(() => wikiMarkdown.render(content.value, path.value))
const initialLoading = ref(false)
const submitting = ref(false)
const error = ref<string | null>(null)
const editingId = ref<number | null>(null)
const missing = ref(false)

const canSubmit = computed(() => path.value.trim().length > 0 && content.value.trim().length > 0 && !submitting.value)
const heading = computed(() => (isEdit.value ? 'Edit page' : 'New page'))

// Looked up live so the breadcrumb stays accurate if the catalog
// updates mid-edit (e.g. a broadcast from another tab). Falls back to
// the path when the catalog hasn't loaded yet — the breadcrumb stays
// useful as `← Back to entities/foo.md` instead of blanking.
const editingTitle = computed(() => {
  const match = pages.value.find((p) => p.path === editingPath.value)
  return match?.title || editingPath.value
})

onMounted(async () => {
  if (!wikiPages.loaded) await wikiPages.fetchAll()
  if (!isEdit.value) return

  initialLoading.value = true
  try {
    const match = pages.value.find((p) => p.path === editingPath.value)
    if (!match) {
      missing.value = true
      return
    }
    editingId.value = match.id
    path.value = match.path
    const detail = await wikiPages.fetchContent(match.id)
    content.value = detail?.content ?? ''
  } finally {
    initialLoading.value = false
  }
})

async function submit() {
  if (!canSubmit.value) return
  submitting.value = true
  error.value = null
  try {
    const result =
      isEdit.value && editingId.value != null
        ? await wikiPages.updatePage(editingId.value, { content: content.value })
        : await wikiPages.createPage({ path: path.value.trim(), content: content.value })
    // Navigate to the viewer with the deep-link path so the SPA picks up
    // the new page and the URL stays shareable. Push a plain path string
    // rather than `{ name, params }` — the catch-all route encodes `/`
    // inside params as `%2F`, which breaks the deep-link resolution.
    await router.push(`/p/${result.path}`)
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'save failed'
  } finally {
    submitting.value = false
  }
}

function cancel() {
  // String paths everywhere so the destination format stays
  // consistent across the file (no mixed string/named-route style).
  router.push(isEdit.value && editingPath.value ? `/p/${editingPath.value}` : '/')
}
</script>

<template>
  <RouteShell>
    <!-- Edit-mode contextual back: routes to the source page so the
         user lands exactly where they came from when they clicked
         Edit. New-page mode has no source to return to, so the slot
         is unused and the nav rail's Today entry serves the "go home"
         affordance. Lives in RouteShell's #back slot so the
         affordance is in the same place as every other contextual
         back-link in the app. -->
    <template v-if="isEdit && editingPath" #back>
      <RouterLink
        :to="`/p/${editingPath}`"
        class="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white no-underline transition-colors"
        data-testid="editor-back"
      >
        <span aria-hidden="true">←</span>
        <span class="truncate max-w-[260px]">Back to {{ editingTitle }}</span>
      </RouterLink>
    </template>

    <section class="max-w-3xl w-full mx-auto pt-2 sm:pt-6 pb-12">
      <h1 class="page-title mb-4">{{ heading }}</h1>

      <div v-if="currentUser && !currentUser.admin" class="text-white/60 text-sm">
        You need admin access to author pages.
      </div>

      <!-- Form-shaped skeleton: a path-input bar plus a tall textarea
           block so the editor's eventual layout is foreshadowed instead
           of spinning on a blank screen. -->
      <div v-else-if="initialLoading" class="flex flex-col gap-6 pt-2" data-testid="editor-skeleton">
        <NSkeleton text :repeat="1" class="!h-10" />
        <NSkeleton text :repeat="14" class="!mb-2" />
      </div>

      <div v-else-if="isEdit && missing" class="text-white/60 text-sm">
        No page exists at <code class="font-mono">{{ editingPath }}</code
        >. Use <RouterLink to="/p/new" class="underline">New page</RouterLink> to create it.
      </div>

      <NCard v-else>
        <NForm @submit.prevent="submit">
          <NFormItem label="Path" :feedback="pathHint">
            <NInput
              v-model:value="path"
              placeholder="entities/my-page.md"
              :disabled="isEdit"
              :input-props="{ autocomplete: 'off', spellcheck: false }"
              data-testid="page-path"
            />
          </NFormItem>

          <NFormItem label="Markdown" :feedback="error ?? undefined" :validation-status="error ? 'error' : undefined">
            <NTabs type="line" size="small" animated class="w-full">
              <NTabPane name="write" tab="Write">
                <NInput
                  v-model:value="content"
                  type="textarea"
                  :autosize="{ minRows: 16, maxRows: 40 }"
                  placeholder="# Title&#10;&#10;Your markdown here…"
                  :input-props="{ spellcheck: false }"
                  data-testid="page-content"
                />
              </NTabPane>
              <NTabPane name="preview" tab="Preview">
                <article
                  v-if="content.trim()"
                  class="wiki-markdown text-sm border border-white/10 rounded p-3 min-h-[16rem]"
                  data-testid="page-preview"
                  v-html="renderedPreview"
                />
                <p v-else class="text-white/40 text-sm py-8">Nothing to preview yet.</p>
              </NTabPane>
            </NTabs>
          </NFormItem>

          <!-- Form action pair — Cancel + Create/Save grouped at the
               bottom-right of the form so the destructive-vs-confirm
               pair stays together. Same shape as
               AddToWikiDrawer and LintNoteEditModal. -->
          <NSpace justify="end">
            <NButton :disabled="submitting" data-testid="page-cancel" @click="cancel"> Cancel </NButton>
            <NButton
              type="primary"
              attr-type="submit"
              :loading="submitting"
              :disabled="!canSubmit"
              data-testid="page-save"
            >
              {{ isEdit ? 'Save' : 'Create page' }}
            </NButton>
          </NSpace>
        </NForm>
      </NCard>
    </section>
  </RouteShell>
</template>
